/**
 * Escalations screen — admin queue for human-review items produced by
 * the agentic pipeline (prospecting → outreach → registration →
 * integration → customer_service → inference_signup).
 *
 * Mirrors the legacy EscalationQueue view on app.nurev.io with one
 * structural cleanup: the legacy detail modal referenced a `creator`
 * variable that was never defined in scope (copy-paste artefact from
 * the CustomerService rename); the publisher info block was therefore
 * dead. Re-wired here as `publisher` against the populated reference.
 *
 * Polling: stats + list both refresh every 60s. Mutation invalidates
 * both queries so the badge counts stay live without a manual refetch.
 */
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import {
  Escalation,
  EscalationPriority,
  EscalationStatus,
  ProspectRef,
  PublisherRef,
  SourceAgent
} from '@/api/escalations';
import {
  useEscalations,
  useEscalationStats,
  useUpdateEscalation
} from '@/hooks/useEscalations';
import {
  ActionButton,
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime } from '@/lib/format';

const SOURCE_LABEL: Record<SourceAgent, string> = {
  prospecting: 'Prospecting',
  outreach: 'Outreach',
  registration: 'Registration',
  integration: 'Integration',
  customer_service: 'Customer Service',
  inference_signup: 'Inference Signup'
};

const STATUS_OPTIONS: Array<{ value: EscalationStatus | ''; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: '', label: 'All Statuses' }
];

const PRIORITY_OPTIONS: Array<{ value: EscalationPriority | ''; label: string }> = [
  { value: '', label: 'All Priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' }
];

const SOURCE_OPTIONS: Array<{ value: SourceAgent | ''; label: string }> = [
  { value: '', label: 'All Sources' },
  { value: 'prospecting', label: 'Prospecting' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'registration', label: 'Registration' },
  { value: 'integration', label: 'Integration' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'inference_signup', label: 'Inference Signup' }
];

// ─── Stats overview ───────────────────────────────────────────────────────

function StatsOverview() {
  const { data, isLoading } = useEscalationStats();
  const stats = data?.byStatus || {};
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-5 lg:gap-4">
      <StatCard
        label="Open"
        value={stats.open || 0}
        color="text-amber-300"
        loading={isLoading}
      />
      <StatCard
        label="Assigned"
        value={stats.assigned || 0}
        color="text-sky-300"
        loading={isLoading}
      />
      <StatCard
        label="Resolved"
        value={stats.resolved || 0}
        color="text-emerald-300"
        loading={isLoading}
      />
      <StatCard
        label="Dismissed"
        value={stats.dismissed || 0}
        loading={isLoading}
      />
      <StatCard
        label="Last 24h"
        value={data?.last24h || 0}
        color="text-amber-200"
        loading={isLoading}
      />
    </div>
  );
}

// ─── Detail modal ─────────────────────────────────────────────────────────

interface DetailModalProps {
  escalation: Escalation;
  onClose: () => void;
}

function DetailModal({ escalation, onClose }: DetailModalProps) {
  const update = useUpdateEscalation();
  const [notes, setNotes] = useState(escalation.notes || '');
  const [assignedTo, setAssignedTo] = useState(escalation.assignedTo || '');
  const [error, setError] = useState<string | null>(null);

  // Esc closes
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const prospect: ProspectRef | null =
    typeof escalation.prospectId === 'object' && escalation.prospectId !== null
      ? (escalation.prospectId as ProspectRef)
      : null;
  const publisher: PublisherRef | null =
    typeof escalation.publisherId === 'object' && escalation.publisherId !== null
      ? (escalation.publisherId as PublisherRef)
      : null;

  const contextEntries = escalation.context
    ? Object.entries(escalation.context)
    : [];

  const handleAction = async (status: EscalationStatus) => {
    setError(null);
    try {
      await update.mutateAsync({
        id: escalation._id,
        status,
        notes,
        assignedTo: assignedTo || undefined
      });
      onClose();
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Update failed.';
      setError(msg);
    }
  };

  const isTerminal =
    escalation.status === 'resolved' || escalation.status === 'dismissed';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-nurev-border bg-nurev-surface-lift p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-nurev-text">{escalation.reason}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              <Badge value={escalation.priority} variant="priority" />
              <Badge value={escalation.status} />
              <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                {SOURCE_LABEL[escalation.sourceAgent] || escalation.sourceAgent}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-nurev-muted hover:bg-white/5 hover:text-nurev-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {prospect && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Prospect</SectionLabel>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <KV label="Domain">{prospect.domain || '—'}</KV>
              <KV label="Company">{prospect.companyName || '—'}</KV>
              <KV label="Contact">{prospect.contactName || '—'}</KV>
              <KV label="Email">{prospect.contactEmail || '—'}</KV>
              <KV label="Status">{prospect.status || '—'}</KV>
              {prospect.score !== undefined && (
                <KV label="Score">{prospect.score}</KV>
              )}
            </div>
          </Card>
        )}

        {publisher && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Publisher</SectionLabel>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <KV label="Name">{publisher.name || '—'}</KV>
              <KV label="Email">{publisher.email || '—'}</KV>
              {publisher.companyName && (
                <KV label="Company">{publisher.companyName}</KV>
              )}
            </div>
          </Card>
        )}

        {contextEntries.length > 0 && (
          <Card className="mb-4 bg-nurev-surface">
            <SectionLabel>Context</SectionLabel>
            <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] leading-relaxed text-nurev-text-soft">
              {JSON.stringify(escalation.context, null, 2)}
            </pre>
          </Card>
        )}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Assigned to
            </label>
            <input
              type="text"
              value={assignedTo}
              onChange={e => setAssignedTo(e.target.value)}
              disabled={isTerminal}
              placeholder="Name or email"
              className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-[10px] uppercase text-nurev-muted">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={isTerminal}
              rows={3}
              className="w-full resize-none rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none disabled:opacity-50"
              placeholder="Add notes for the next person to see (audit-logged)…"
            />
          </div>

          {error && <p className="text-xs text-red-300">{error}</p>}

          {!isTerminal && (
            <div className="flex flex-wrap gap-2 pt-1">
              {escalation.status === 'open' && (
                <ActionButton
                  variant="info"
                  size="sm"
                  disabled={update.isPending}
                  onClick={() => handleAction('assigned')}
                >
                  Assign
                </ActionButton>
              )}
              <ActionButton
                variant="positive"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleAction('resolved')}
              >
                Resolve
              </ActionButton>
              <ActionButton
                variant="neutral"
                size="sm"
                disabled={update.isPending}
                onClick={() => handleAction('dismissed')}
              >
                Dismiss
              </ActionButton>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between text-[10px] text-nurev-muted">
          <span>Created {fmtDateTime(escalation.createdAt)}</span>
          {escalation.resolvedAt && (
            <span>Closed {fmtDateTime(escalation.resolvedAt)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="text-nurev-muted">{label}:</span>{' '}
      <span className="text-nurev-text">{children}</span>
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function EscalationsScreen() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<EscalationStatus | ''>('open');
  const [priorityFilter, setPriorityFilter] = useState<EscalationPriority | ''>('');
  const [sourceFilter, setSourceFilter] = useState<SourceAgent | ''>('');
  const [selected, setSelected] = useState<Escalation | null>(null);

  const params = useMemo(
    () => ({
      page,
      limit: 20,
      sort: '-createdAt',
      status: statusFilter,
      priority: priorityFilter,
      sourceAgent: sourceFilter
    }),
    [page, statusFilter, priorityFilter, sourceFilter]
  );
  const { data, isLoading } = useEscalations(params);
  const items = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  const resetPage = () => setPage(1);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Escalations
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            Human-review items from the agentic pipeline. Assign, resolve, or
            dismiss with audit trail.
          </p>
        </div>
        <a
          href="https://app.nurev.io/escalations"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      <StatsOverview />

      <div className="flex flex-wrap gap-2">
        <Select
          value={statusFilter}
          onChange={v => {
            setStatusFilter(v);
            resetPage();
          }}
          options={STATUS_OPTIONS}
        />
        <Select
          value={priorityFilter}
          onChange={v => {
            setPriorityFilter(v);
            resetPage();
          }}
          options={PRIORITY_OPTIONS}
        />
        <Select
          value={sourceFilter}
          onChange={v => {
            setSourceFilter(v);
            resetPage();
          }}
          options={SOURCE_OPTIONS}
        />
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Reason</th>
                <th className="px-4 py-3 font-medium">Prospect / Publisher</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Assigned</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-nurev-muted">
                    No escalations match these filters.
                  </td>
                </tr>
              ) : (
                items.map(item => {
                  const prospect: ProspectRef | null =
                    typeof item.prospectId === 'object' && item.prospectId !== null
                      ? (item.prospectId as ProspectRef)
                      : null;
                  const publisher: PublisherRef | null =
                    typeof item.publisherId === 'object' && item.publisherId !== null
                      ? (item.publisherId as PublisherRef)
                      : null;
                  const target =
                    prospect?.domain ||
                    prospect?.companyName ||
                    publisher?.name ||
                    publisher?.email ||
                    '—';
                  return (
                    <tr
                      key={item._id}
                      className="cursor-pointer border-b border-nurev-border/50 transition-colors hover:bg-white/[0.02]"
                      onClick={() => setSelected(item)}
                    >
                      <td className="px-4 py-3">
                        <Badge value={item.priority} variant="priority" />
                      </td>
                      <td className="px-4 py-3 text-xs text-nurev-muted">
                        {SOURCE_LABEL[item.sourceAgent] || item.sourceAgent}
                      </td>
                      <td className="max-w-[300px] truncate px-4 py-3 text-nurev-text">
                        {item.reason}
                      </td>
                      <td className="px-4 py-3 text-xs text-nurev-text-soft">
                        {target}
                      </td>
                      <td className="px-4 py-3">
                        <Badge value={item.status} />
                      </td>
                      <td className="px-4 py-3 text-xs text-nurev-muted">
                        {item.assignedTo || '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-xs text-nurev-muted">
                        {fmtDate(item.createdAt)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </div>
        )}
      </Card>

      {selected && (
        <DetailModal escalation={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
